import { useUiText } from "../../locale/UiLocale";
import { type ReactNode, useState } from "react";
import type { IconName } from "../../icons";
import { Modal } from "../Modal/Modal";

type ConfirmTone = "default" | "danger" | "warning";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: ConfirmTone;
  icon?: IconName;
  /** May return a promise; the button shows a spinner meanwhile. */
  onConfirm: () => unknown;
  onClose: () => void;
  confirmDisabled?: boolean;
}

const TONE_ICON: Record<ConfirmTone, IconName> = { default: "info", danger: "alertCircle", warning: "alert" };

/** Confirmation for consequential actions (disable, reset, delete). */
export function ConfirmDialog({
  open,
  title,
  description,
  children,
  confirmLabel,
  cancelLabel = "Cancel",
  tone = "default",
  icon,
  onConfirm,
  onClose,
  confirmDisabled,
}: ConfirmDialogProps): React.JSX.Element | null {
  const t = useUiText();
  const [busy, setBusy] = useState(false);

  function confirm(): void {
    if (busy) return;
    setBusy(true);
    void Promise.resolve(onConfirm()).finally(() => setBusy(false));
  }

  return (
    <Modal
      open={open}
      size="sm"
      title={title}
      description={description}
      tone={tone}
      icon={icon ?? TONE_ICON[tone]}
      onClose={() => {
        if (!busy) onClose();
      }}
      actions={
        <>
          <button type="button" className="nb-btn nb-btn-secondary" onClick={onClose} disabled={busy}>
            {t(cancelLabel)}
          </button>
          <button
            type="button"
            className={`nb-btn ${tone === "danger" ? "nb-btn-danger" : "nb-btn-primary"}`}
            aria-busy={busy || undefined}
            disabled={confirmDisabled}
            onClick={confirm}
          >
            {busy ? <span className="nb-spinner" aria-hidden="true" /> : null}
            <span>{confirmLabel}</span>
          </button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
